import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { fetchApiWithError } from '@upright/ui/api/common/fetchers/fetch-with-error';
import { getFromApi } from '@upright/ui/api/common/fetchers/request-api';
import { Options, optionsToString } from '@upright/ui/api/common/util';
import { stringify } from 'querystring';
import { useQuery } from '@tanstack/react-query';
import { ModelEntityType } from '../../../../../common/model/input/types';
import { ComparisonWithResult } from '../../../../../common/model/output/common-sense-comparisons/types';
import { DebugProductImpactScore } from '../../../../../common/model/output/scores/debug-impact-figures/types';
import { ProductReferenceFigures } from '../../../../../common/model/output/scores/reference-figures/types';
import {
  FetchSingleModelTopContributorsOptions,
  GetTopContributorsResponse,
} from '../../../../../common/model/output/scores/top-contributors/api-types';
import { ImpactScoresByIVV, TermPairScoreDatum } from '../../../../../common/model/output/scores/types';
import { Valence, ValueChain } from '../../../../../common/model/types';

export async function fetchAggregateImpactScores(version: string) {
  return await getFromApi<ImpactScoresByIVV>(`/scores/${version}/aggregate`);
}

/**
 * Fetch simple product score for an (I)impact, (V)alue chain, (V)alence combination
 */
export async function fetchProductScoresForIVV(
  impactCode: string,
  valueChain: ValueChain | undefined,
  valence: Valence,
  version: string,
) {
  const vc = valueChain === undefined ? 'all' : valueChain;
  return await getFromApi<DebugProductImpactScore[]>(`/scores/${version}/impact/${impactCode}/${vc}/${valence}`);
}

export async function fetchProductReferenceFigures(version: string) {
  return await fetchApiWithError<ProductReferenceFigures>(`/scores/${version}/product-reference-figures`);
}

export async function fetchTermPairScores(
  productCode: string | undefined,
  impactCode: string | undefined,
  valueChain: ValueChain | undefined,
  valence: Valence | undefined,
  _version: string,
): Promise<any> {
  const params = stringify({
    productCode,
    impactCode,
    valueChain,
    valence,
  });
  const version = 'master'; // TODO: currently ignores version as they are not part of snapshots
  const url = `/scores/${version}/term-pair?${params}`;
  return await getFromApi<TermPairScoreDatum[]>(url);
}

export async function fetchImpactComparisons(version: string): Promise<any> {
  const url = `/scores/${version}/impact-comparisons`;
  return await getFromApi<ComparisonWithResult[]>(url);
}

export function useTopContributors(
  entityType: ModelEntityType | undefined,
  variantOrResult: string | number | undefined,
  impactCategory: string | number | undefined,
  valence: string,
  options?: FetchSingleModelTopContributorsOptions,
) {
  return useQuery({
    queryKey: ['topContributors', entityType, variantOrResult, impactCategory, valence, options],
    queryFn: async () => await fetchTopContributors(entityType!, variantOrResult!, impactCategory!, valence, options),
    enabled: !!entityType && !!impactCategory && !!variantOrResult && options?.enabled !== false,
  });
}

async function fetchTopContributors(
  entityType: ModelEntityType,
  variantOrResult: string | number,
  impactCategory: string | number,
  valence: string,
  options?: FetchSingleModelTopContributorsOptions,
) {
  return await fetchOrThrow<GetTopContributorsResponse>(
    `/results/top-contributors/${entityType}/${variantOrResult}/${impactCategory}/${valence}${optionsToString(
      options as Options,
    )}`,
  );
}
